import type { ParcelDTO } from "./catasto";
import { apiGet, apiPost, apiPostBlob, buildQuery } from "./http";
import type { ProspectDetail, ProspectListResponse, ProspectWorkedResult, WorkedFilter } from "../../api/contract";

// Shape spostate in contract.ts (single source of truth REST) e ri-esportate
// per compatibilità: prima erano duplicate a mano rispetto ad api/prospects.ts
// e una divergenza non veniva intercettata da nessun typecheck.
export type {
  MatchCandidate,
  OmonimiSituation,
  ProspectDetail,
  ProspectImmobile,
  ProspectHerobotSync,
  ProspectImmobileSummary,
  ProspectListItem,
  ProspectListResponse,
  ProspectProvinceCount,
  ProspectTitolare,
  ProspectWorkedResult,
  WorkedFilter,
} from "../../api/contract";

export interface ProspectListQuery {
  limit?: number;
  offset?: number;
  status?: string | null;
  provincia?: string | null;
  q?: string | null;
  minImmobili?: number | null;
  hasXml?: boolean | null;
  hasDocument?: boolean | null;
  /** casaVuota=true → solo necrologi senza coniuge superstite rilevato. */
  casaVuota?: boolean | null;
  discarded?: "exclude" | "only" | "include" | null;
  worked?: WorkedFilter | null;
}

export function listProspects(q: ProspectListQuery = {}): Promise<ProspectListResponse> {
  return apiGet<ProspectListResponse>(`/api/prospects${buildQuery(q as Record<string, unknown>)}`);
}

export function getProspect(id: number | string): Promise<ProspectDetail> {
  return apiGet<ProspectDetail>(`/api/prospects/${id}`);
}

/** Same filters as the list, without paging: the file holds every prospect the list shows (up to 5000). */
export function prospectsExportUrl(q: Omit<ProspectListQuery, "limit" | "offset"> = {}): string {
  return `/api/prospects/export${buildQuery(q as Record<string, unknown>)}`;
}

export function exportSelectedProspects(ids: number[], q: Omit<ProspectListQuery, "limit" | "offset"> = {}): Promise<Blob> {
  return apiPostBlob(prospectsExportUrl(q), { ids });
}

export function prospectVisuraXmlUrl(id: number | string): string {
  return `/api/prospects/${id}/visura.xml`;
}

export function backfillProspectImmobili(): Promise<{ scanned: number; updated: number }> {
  return apiPost<{ scanned: number; updated: number }>("/api/prospects/backfill");
}

export function bulkSendProspectsToHerobot(input: {
  limit?: number;
  note?: string | null;
} = {}): Promise<{
  scanned: number;
  sent: number;
  failed: number;
  results: Array<{ id: number; ok: boolean; herobotId: number | null; error: string | null }>;
}> {
  return apiPost("/api/prospects/herobot/bulk", {
    limit: input.limit ?? 10,
    note: input.note ?? null,
  });
}

export interface ProspectImmobileGeo {
  index: number;
  ref: {
    comuneCode: string | null;
    comuneName: string | null;
    foglio: string | null;
    particella: string | null;
  };
  parcel: ParcelDTO | null;
  centroid: { lat: number; lon: number } | null;
  geocode: { formatted: string; precision: string } | null;
  resolved: "ref" | "name" | "geocode" | "none";
}

export interface ProspectImmobiliGeoResponse {
  items: ProspectImmobileGeo[];
  bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null;
}

export function getProspectImmobiliGeo(id: number | string): Promise<ProspectImmobiliGeoResponse> {
  return apiGet<ProspectImmobiliGeoResponse>(`/api/prospects/${id}/immobili-geo`);
}

export interface ProspectConfirmMatchResult extends ProspectDetail {
  /** true se un task SISTER è stato davvero rimesso in coda per la visura;
   * false = CF salvato ma nessun task da rilanciare (riaccodare da Necrologi). */
  requeued: boolean;
}

/** Omonimo scelto: per codice fiscale, o per id della riga SISTER quando il CF manca. */
export type ProspectMatchChoice = { cf: string } | { omonimoId: string };

export function confirmProspectMatch(
  id: number | string,
  choice: ProspectMatchChoice,
): Promise<ProspectConfirmMatchResult> {
  const body = "cf" in choice ? { matchedCf: choice.cf } : { omonimoId: choice.omonimoId };
  return apiPost<ProspectConfirmMatchResult>(`/api/prospects/${id}/confirm-match`, body);
}

export function markProspectNoMatch(
  id: number | string,
  reason?: string | null,
): Promise<ProspectDetail> {
  return apiPost<ProspectDetail>(`/api/prospects/${id}/mark-no-match`, { reason: reason ?? undefined });
}

export function discardProspect(id: number | string): Promise<ProspectDetail> {
  return apiPost<ProspectDetail>(`/api/prospects/${id}/discard`);
}

export function restoreProspect(id: number | string): Promise<ProspectDetail> {
  return apiPost<ProspectDetail>(`/api/prospects/${id}/restore`);
}

export function setProspectWorked(id: number | string, worked: boolean): Promise<ProspectWorkedResult> {
  return apiPost<ProspectWorkedResult>(`/api/prospects/${id}/worked`, { worked });
}

export function sendProspectToHerobot(
  id: number | string,
  note?: string | null,
): Promise<ProspectDetail> {
  return apiPost<ProspectDetail>(`/api/prospects/${id}/herobot`, { note: note?.trim() || null });
}
